import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import { listStaffResult } from '@/modules/staff/services/staff.service';
import {
  deleteDocument as deleteDocumentMutation,
  listDocumentsResult,
  persistDocuments,
  updateDocumentMetadata as updateDocumentMetadataMutation,
} from '@/modules/documents/services';
import { DOCUMENT_CATEGORIES, calculateDocumentStats } from '@/modules/documents/utils';
import { useDocumentsFilters } from './useDocumentsFilters';

const DOCUMENTS_AUTH_ERRORS = new Set([
  'DOCUMENTS_AUTH_REQUIRED',
  'DOCUMENTS_SESSION_REFRESH_REQUIRED',
  'DOCUMENTS_SESSION_EXPIRED',
]);

function upsertDocumentsById(documents, document) {
  const documentId = document?.id;
  if (documentId == null) {
    return documents;
  }

  const existingIndex = documents.findIndex((item) => String(item.id) === String(documentId));
  if (existingIndex === -1) {
    return [document, ...documents];
  }

  return documents.map((item, index) => (index === existingIndex ? document : item));
}

function normalizeRelationId(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

function buildClientsById(clients) {
  return new Map(
    clients
      .filter((client) => normalizeRelationId(client.id))
      .map((client) => [normalizeRelationId(client.id), client])
  );
}

function resolveClientDisplayName(client) {
  return client?.name || client?.company || 'Cliente senza nome';
}

function resolveDocumentClient(document, clientsById) {
  const clientId = normalizeRelationId(document?.clientId);
  const legacyName = document?.client ? String(document.client).trim() : '';

  if (!clientId) {
    return {
      clientResolvedName: legacyName || 'Cliente non risolto',
      clientLegacyName: legacyName,
      clientResolutionStatus: legacyName ? 'legacy_fallback' : 'missing_client_id',
    };
  }

  const catalogClient = clientsById.get(clientId);
  if (catalogClient) {
    return {
      clientResolvedName: resolveClientDisplayName(catalogClient),
      clientLegacyName: legacyName,
      clientResolutionStatus: 'resolved',
    };
  }

  return {
    clientResolvedName: legacyName || 'Cliente non risolto',
    clientLegacyName: legacyName,
    clientResolutionStatus: 'unresolved_client_reference',
  };
}

function enrichDocumentsWithClientResolution(documents, clients) {
  const clientsById = buildClientsById(clients);

  return documents.map((document) => {
    const resolution = resolveDocumentClient(document, clientsById);

    return {
      ...document,
      ...resolution,
      client: resolution.clientResolvedName,
    };
  });
}

export function useDocumentsLibraryData({ initialClientId = '' } = {}) {
  const { logout } = useSession();
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [documentsError, setDocumentsError] = useState('');
  const [documentsAuthRequired, setDocumentsAuthRequired] = useState(false);
  const [documentsModuleDisabled, setDocumentsModuleDisabled] = useState(false);
  const [documentsTenantDatabaseBlocked, setDocumentsTenantDatabaseBlocked] = useState(false);
  const [usingCachedSnapshot, setUsingCachedSnapshot] = useState(false);
  const [documentsSnapshotStale, setDocumentsSnapshotStale] = useState(false);
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffError, setStaffError] = useState('');
  const filters = useDocumentsFilters(documents);
  const { setSelectedClientId } = filters;
  const normalizedInitialClientId = normalizeRelationId(initialClientId);
  const appliedInitialClientId = useRef('');

  useEffect(() => {
    if (!normalizedInitialClientId || appliedInitialClientId.current === normalizedInitialClientId) return;
    appliedInitialClientId.current = normalizedInitialClientId;
    setSelectedClientId(normalizedInitialClientId);
  }, [normalizedInitialClientId, setSelectedClientId]);

  const categories = useMemo(
    () => [
      'Tutti',
      ...new Set([
        ...DOCUMENT_CATEGORIES,
        ...documents.map((doc) => doc.category).filter(Boolean),
      ]),
    ],
    [documents]
  );
  const stats = useMemo(() => calculateDocumentStats(documents), [documents]);

  async function reloadDocuments() {
    const documentsResult = await listDocumentsResult();
    if (DOCUMENTS_AUTH_ERRORS.has(documentsResult.error)) {
      logout();
    }
    const resolvedDocuments = enrichDocumentsWithClientResolution(documentsResult.data.documents, clients);

    setDocuments(resolvedDocuments);
    setDocumentsError(documentsResult.ok ? '' : documentsResult.message);
    setDocumentsAuthRequired(DOCUMENTS_AUTH_ERRORS.has(documentsResult.error));
    setDocumentsModuleDisabled(documentsResult.error === 'DOCUMENTS_MODULE_DISABLED');
    setDocumentsTenantDatabaseBlocked(
      documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_NOT_READY'
        || documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_METADATA_MISSING'
        || documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_UNAVAILABLE'
    );
    setUsingCachedSnapshot(Boolean(documentsResult.data.hasCachedSnapshot));
    setDocumentsSnapshotStale(Boolean(documentsResult.data.documentsSnapshotStale));
    setIsLoadingDocuments(false);

    return documentsResult;
  }

  useEffect(() => {
    let active = true;

    async function loadDocumentsAndClients() {
      const [documentsResult, clientsResult, staffResult] = await Promise.all([
        listDocumentsResult(),
        listClientsResult(),
        listStaffResult(),
      ]);
      if (active) {
        const authRequired =
          DOCUMENTS_AUTH_ERRORS.has(documentsResult.error) ||
          clientsResult.error === 'CLIENTS_AUTH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_SESSION_REFRESH_REQUIRED' ||
          clientsResult.error === 'CLIENTS_SESSION_EXPIRED' ||
          staffResult.error === 'STAFF_AUTH_REQUIRED' ||
          staffResult.error === 'STAFF_SESSION_REFRESH_REQUIRED' ||
          staffResult.error === 'STAFF_SESSION_EXPIRED';
        if (authRequired) {
          logout();
        }
        const resolvedClients = clientsResult.ok ? clientsResult.data : [];
        const resolvedDocuments = enrichDocumentsWithClientResolution(
          documentsResult.data.documents,
          resolvedClients
        );

        setClients(resolvedClients);
        setStaff(staffResult.ok ? staffResult.data : []);
        setClientsUnavailable(!clientsResult.ok);
        setClientsError(clientsResult.ok ? '' : clientsResult.error || '');
        setStaffUnavailable(!staffResult.ok);
        setStaffError(staffResult.ok ? '' : staffResult.message || staffResult.error || '');
        setDocuments(resolvedDocuments);
        setDocumentsError(documentsResult.ok ? '' : documentsResult.message);
        setDocumentsAuthRequired(DOCUMENTS_AUTH_ERRORS.has(documentsResult.error));
        setDocumentsModuleDisabled(documentsResult.error === 'DOCUMENTS_MODULE_DISABLED');
        setDocumentsTenantDatabaseBlocked(
          documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_NOT_READY'
            || documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_METADATA_MISSING'
            || documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_UNAVAILABLE'
        );
        setUsingCachedSnapshot(Boolean(documentsResult.data.hasCachedSnapshot));
        setDocumentsSnapshotStale(Boolean(documentsResult.data.documentsSnapshotStale));
        setIsLoadingDocuments(false);
      }
    }

    loadDocumentsAndClients();

    return () => {
      active = false;
    };
  }, []);

  async function registerDocument(document) {
    const enrichedDocument = enrichDocumentsWithClientResolution([document], clients)[0];
    const documentsResult = await listDocumentsResult();
    if (documentsResult.ok) {
      setDocuments(enrichDocumentsWithClientResolution(documentsResult.data.documents, clients));
      setDocumentsError('');
      setDocumentsAuthRequired(false);
      setDocumentsModuleDisabled(false);
      setDocumentsTenantDatabaseBlocked(false);
      setUsingCachedSnapshot(false);
      setDocumentsSnapshotStale(false);
      return documentsResult;
    }

    setDocumentsError(
      'Documento creato sul backend, ma il riallineamento della library non è riuscito. Ricarica la pagina per verificare il file live dal backend.'
    );
    setDocumentsAuthRequired(DOCUMENTS_AUTH_ERRORS.has(documentsResult.error));
    setDocumentsModuleDisabled(documentsResult.error === 'DOCUMENTS_MODULE_DISABLED');
    setDocumentsTenantDatabaseBlocked(
      documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_NOT_READY'
        || documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_METADATA_MISSING'
        || documentsResult.error === 'DOCUMENTS_TENANT_DATABASE_UNAVAILABLE'
    );
    setUsingCachedSnapshot(false);
    setDocumentsSnapshotStale(false);

    return documentsResult;
  }

  async function deleteDocument(documentId) {
    const result = await deleteDocumentMutation(documentId);
    if (!result.ok) {
      return result;
    }

    const nextDocuments = documents.filter((document) => String(document.id) !== String(documentId));
    setDocuments(nextDocuments);
    persistDocuments(nextDocuments);
    setDocumentsError('');
    setUsingCachedSnapshot(false);
    setDocumentsSnapshotStale(false);
    return result;
  }

  async function updateDocumentMetadata(documentId, metadata) {
    const result = await updateDocumentMetadataMutation(documentId, metadata);
    if (!result.ok) {
      return result;
    }

    const updatedDocument = enrichDocumentsWithClientResolution([result.data.document], clients)[0];
    const nextDocuments = upsertDocumentsById(documents, updatedDocument);
    setDocuments(nextDocuments);
    persistDocuments(nextDocuments);
    setDocumentsError('');
    setUsingCachedSnapshot(false);
    setDocumentsSnapshotStale(false);
    return result;
  }

  return {
    documents,
    clients,
    staff,
    clientsUnavailable,
    clientsError,
    staffUnavailable,
    staffError,
    isLoadingDocuments,
    documentsError,
    documentsAuthRequired,
    documentsModuleDisabled,
    documentsTenantDatabaseBlocked,
    documentsCatalogStatus: documentsModuleDisabled
      ? 'module_disabled'
      : documentsAuthRequired
        ? 'auth_required'
        : documentsTenantDatabaseBlocked
          ? 'tenant_database_unavailable'
        : documentsError
          ? usingCachedSnapshot
            ? 'available'
            : 'unavailable'
          : documents.length > 0
            ? 'available'
            : 'empty',
    usingCachedSnapshot,
    documentsSnapshotStale,
    categories,
    stats,
    reloadDocuments,
    registerDocument,
    updateDocumentMetadata,
    deleteDocument,
    ...filters,
  };
}
